/* eslint-disable max-lines */

import classnames from 'classnames'
import { ContactLeapLife, FieldData, FieldKey, LifeInsuranceResultsView } from 'embed/constants'
import Api from 'common/services/Api'
import { Filter, Product } from 'even-ts-static'
import Session from 'common/services/Session'
import { LifeInsuranceOffer, LifeInsuranceProductSubType } from 'embed/constants/models'
import { captureLocalStorageAccessWarning } from 'embed/constants/errors'
import { css } from 'emotion'
import * as React from 'react'
import OnceLoaded from '../../OnceLoaded'
import ThemeConsumer from 'embed/components/ThemeConsumer'
import { AdbBanner } from './AdbBanner'
import { SelectCoverage } from './SelectCoverage'
import { OfferList } from './OfferList'
import { TogglePolicies } from './TogglePolicies'
import { EmptyState } from './EmptyState'
import { QuoteModal } from './QuoteModal'
import AdvertiserDisclosure from '../../AdvertiserDisclosure'

const LightBulbIcon = require('assets/components/light-bulb.svg')
const PhoneIcon = require('assets/components/icon-phone.svg')
const EmailIcon = require('assets/components/email.svg')

const ShaunaIcon = require('assets/images/Shauna_avatar.jpg')

const styles = require('./styles')

export enum LifeInsuranceProductSubTypeOtherPolicy {
  OtherPolicies = 'other_policies',
  NoPolicies = '',
}

export interface LifeInsuranceResultsProps {
  onToggleResultsView: () => void
  allLifeInsuranceOffers: LifeInsuranceOffer[]
  browseViewLifeInsuranceOffers: LifeInsuranceOffer[]
  recommendedViewLifeInsuranceOffers: LifeInsuranceOffer[]
  resultsView: LifeInsuranceResultsView
  applicationEmail: FieldData | undefined
  dispatchUpdateLead: () => void
  dispatchApplicationFieldShowValidationStatus: (fieldKey: FieldKey, show: boolean) => void
  coverageAmount: number
  coverageDuration: number
  companyUuid: string
  onCoverageAmountChange: (value: number) => void
  onDurationChange: (value: number) => void
  loadingCustomizedOffer: boolean
  rateTableUuid: string
  requestACallDisplayed: boolean
  enableCustomBackground: boolean
}

export interface OfferSelectionState {
  tabs: Tab[]
  offers: LifeInsuranceOffer[]
  selectedTab: string
}

interface Tab {
  id: string
  label: string
  disabled: boolean
}

const termLifeOffers: LifeInsuranceOffer[] = []
const adbOffers: LifeInsuranceOffer[] = []
const otherOffers: LifeInsuranceOffer[] = []

const tabOptions = {
  [LifeInsuranceProductSubType.TermLife]: {
    id: LifeInsuranceProductSubType.TermLife,
    label: 'Term Life Insurance Policies',
    disabled: false,
  },
  [LifeInsuranceProductSubType.AccidentalDeathBenefits]: {
    id: LifeInsuranceProductSubType.AccidentalDeathBenefits,
    label: 'Accidental Death Benefits Policies',
    disabled: false,
  },
  [LifeInsuranceProductSubTypeOtherPolicy.OtherPolicies]: {
    id: LifeInsuranceProductSubTypeOtherPolicy.OtherPolicies,
    label: 'Other Policies For You',
    disabled: !otherOffers.length,
  },
}

const filterOffers = (offers: LifeInsuranceOffer[]) => {
  termLifeOffers.splice(0)
  adbOffers.splice(0)
  otherOffers.splice(0)

  const currentTermLifeOffers = offers.filter((offer) => offer.productSubType === LifeInsuranceProductSubType.TermLife)
  const hasTermLifeOffers = currentTermLifeOffers.length

  offers.forEach((offer) => {
    if (offer.productSubType !== LifeInsuranceProductSubType.TermLife) {
      // if there are term_life offers, adb offers go to "other"
      if (!hasTermLifeOffers && offer.productSubType === LifeInsuranceProductSubType.AccidentalDeathBenefits) {
        adbOffers.push(offer)
      } else {
        otherOffers.push(offer)
      }
    }
  })
  termLifeOffers.push(...currentTermLifeOffers)
}

const getOfferOptions = () => ({
  [LifeInsuranceProductSubType.TermLife]: {
    tabs: [
      tabOptions[LifeInsuranceProductSubType.TermLife],
      tabOptions[LifeInsuranceProductSubTypeOtherPolicy.OtherPolicies],
    ],
    offers: termLifeOffers,
    selectedTab: LifeInsuranceProductSubType.TermLife,
  },
  [LifeInsuranceProductSubType.AccidentalDeathBenefits]: {
    tabs: [
      tabOptions[LifeInsuranceProductSubType.AccidentalDeathBenefits],
      tabOptions[LifeInsuranceProductSubTypeOtherPolicy.OtherPolicies],
    ],
    offers: adbOffers,
    selectedTab: LifeInsuranceProductSubType.AccidentalDeathBenefits,
  },
  [LifeInsuranceProductSubTypeOtherPolicy.OtherPolicies]: {
    tabs: [
      ...(termLifeOffers.length ? [tabOptions[LifeInsuranceProductSubType.TermLife]] : []),
      ...(adbOffers.length ? [tabOptions[LifeInsuranceProductSubType.AccidentalDeathBenefits]] : []),
      tabOptions[LifeInsuranceProductSubTypeOtherPolicy.OtherPolicies],
    ],
    offers: otherOffers,
    selectedTab: LifeInsuranceProductSubTypeOtherPolicy.OtherPolicies,
  },
  [LifeInsuranceProductSubTypeOtherPolicy.NoPolicies]: { tabs: [], offers: [], selectedTab: '' },
})

const getOfferSelectionType = () => {
  if (termLifeOffers.length) {
    return LifeInsuranceProductSubType.TermLife
  } else if (adbOffers.length) {
    return LifeInsuranceProductSubType.AccidentalDeathBenefits
  } else if (otherOffers.length) {
    return LifeInsuranceProductSubTypeOtherPolicy.OtherPolicies
  }
  return LifeInsuranceProductSubTypeOtherPolicy.NoPolicies
}

const getRemarketingPreference = () => {
  try {
    const quoteModalWasShownValue = localStorage.getItem('life_remarketing_submitted')
    const quoteModalWasShown = quoteModalWasShownValue === null ? null : JSON.parse(quoteModalWasShownValue)

    return quoteModalWasShown
  } catch (err) {
    console.error("error retrieving life_remarketing_submitted's key value from localStorage", err)
    captureLocalStorageAccessWarning(err)
  }
  return null
}

const setRemarketingPreference = (value: boolean) => {
  try {
    localStorage.setItem('life_remarketing_submitted', JSON.stringify(value))
  } catch (err) {
    console.error("error updating life_remarketing_submitted's key value from localStorage", err)
    captureLocalStorageAccessWarning(err)
  }
}

export const LifeInsuranceResults: React.FunctionComponent<LifeInsuranceResultsProps> = ({
  applicationEmail,
  allLifeInsuranceOffers,
  dispatchUpdateLead,
  dispatchApplicationFieldShowValidationStatus,
  onToggleResultsView,
  resultsView,
  coverageAmount,
  coverageDuration,
  companyUuid,
  onCoverageAmountChange,
  onDurationChange,
  loadingCustomizedOffer,
  rateTableUuid,
  requestACallDisplayed,
  enableCustomBackground,
}) => {
  const currentRemarketingPreference = getRemarketingPreference()
  const [remarketingModalWasShown, setRemarketingModalWasShown] = React.useState(currentRemarketingPreference !== null)
  const [isLoading, setIsLoading] = React.useState(false)

  const [offerSelection, setOfferSelection] = React.useState<OfferSelectionState>({
    tabs: [],
    offers: [],
    selectedTab: '',
  })

  const offerOptions = getOfferOptions()

  const changeTab = (id: string) => setOfferSelection(offerOptions[id])

  const hasOffers = !!offerSelection.offers.length && !!offerSelection.tabs.length

  const isTermLife = offerSelection.selectedTab === LifeInsuranceProductSubType.TermLife

  const hasTermLifeOffers = termLifeOffers.length > 0

  const shouldShowSelection = (isTermLife || !hasOffers) && coverageAmount > 0 && coverageDuration > 0

  const containerStyle = enableCustomBackground ? { backgroundColor: 'unset' } : {}

  const handleRemarketingEmailDeclined = () => {
    setRemarketingPreference(false)
    setRemarketingModalWasShown(true)

    // Don't show email error badge on the customize page if the modal is submitting
    if (!isLoading) {
      dispatchApplicationFieldShowValidationStatus(Filter.Email, false)
    }
  }

  const handleRemarketingEmailSubmitted = async () => {
    setIsLoading(true)
    const emailLead = applicationEmail?.value?.toString()
    await Promise.all([
      Api.post('/lifeInsurance/remarketing', {
        email: emailLead,
        rateTableUuid: rateTableUuid,
        companyUuid: companyUuid,
      }),
      dispatchUpdateLead(),
    ])

    setRemarketingPreference(true)
    setRemarketingModalWasShown(true)
    setIsLoading(false)
  }

  React.useEffect(() => {
    const offers: LifeInsuranceOffer[] = allLifeInsuranceOffers
    filterOffers(offers)
    setOfferSelection(offerOptions[getOfferSelectionType()])
    Session.setReturningLead()
  }, [allLifeInsuranceOffers, resultsView])

  const isLeaplifeEmailPage = location.href.match('leaplife-email') !== null
  const showRemarketingModal = !isLeaplifeEmailPage && hasOffers && !remarketingModalWasShown

  return (
    <OnceLoaded product={Product.LifeInsurance}>
      {showRemarketingModal && (
        <QuoteModal
          isLoading={isLoading}
          isValid={applicationEmail ? !!applicationEmail.isValid : false}
          handleRemarketingEmailSubmitted={handleRemarketingEmailSubmitted}
          handleRemarketingEmailDeclined={handleRemarketingEmailDeclined}
        />
      )}
      <ThemeConsumer>
        {({ colorBorder, borderRadiusSmall }) => (
          <div
            className={classnames(
              styles.container,
              css`
                border-color: ${colorBorder};
                border-radius: ${borderRadiusSmall};
              `,
            )}
            style={containerStyle}
          >
            <div className={styles.contentContainer}>
              <AdvertiserDisclosure className={styles.advertiserDisclosure} />
              <div className={styles.resultsContainer}>
                <TogglePolicies
                  offerSelection={offerSelection}
                  changeTab={changeTab}
                  isCustomizable={isTermLife}
                  hasOffers={hasOffers}
                  totalOtherOffers={otherOffers.length}
                  loadingCustomizedOffer={loadingCustomizedOffer}
                />
                <AdbBanner
                  totalAdbOffers={adbOffers.length}
                  totalTermLifeOffers={termLifeOffers.length}
                  selectedTab={offerSelection.selectedTab}
                />
                {shouldShowSelection && (
                  <SelectCoverage
                    coverageAmount={coverageAmount}
                    onCoverageAmountChange={onCoverageAmountChange}
                    coverageDuration={coverageDuration}
                    onDurationChange={onDurationChange}
                  />
                )}
                <OfferList
                  loadingCustomizedOffer={loadingCustomizedOffer}
                  offers={offerSelection.offers}
                  selectedTab={offerSelection.selectedTab}
                />
                <EmptyState
                  hasOffers={hasOffers}
                  loadingCustomizedOffer={loadingCustomizedOffer}
                  requestACallDisplayed={requestACallDisplayed}
                />
              </div>
              <div className={styles.sidebarContainer}>
                {hasTermLifeOffers && offerSelection.selectedTab === LifeInsuranceProductSubType.TermLife && (
                  <div className={styles.lightBulbMessageContainerMd}>
                    <LightBulbMessage />
                  </div>
                )}
                <ContactMessage />
                <FrequentlyAskedQuestions />
              </div>
            </div>
          </div>
        )}
      </ThemeConsumer>
    </OnceLoaded>
  )
}

export const LightBulbMessage = () => (
  <ThemeConsumer>
    {({ borderRadiusSmall, colorBorder, ctaStyles }) => (
      <div
        className={classnames(
          styles.lightBulbMessageContainer,
          css`
            border-color: ${colorBorder};
            border-radius: ${borderRadiusSmall};
            svg {
              path {
                fill: ${ctaStyles?.backgroundColor};
              }
            }
          `,
        )}
      >
        <LightBulbIcon />
        <p>
          Financial planners recommend at least 10x your annual income and a term length that will cover you until
          retirement.
        </p>
      </div>
    )}
  </ThemeConsumer>
)

const FrequentlyAskedQuestions = () => (
  <div className={styles.faqContainer}>
    <h2>Frequently Asked Questions</h2>
    <h4>What do Term and Coverage mean?</h4>
    <p>
      Term is the amount of time you want to hold the policy for. Common term lengths for level-term policies are 10,
      15, 20, and 30 years. You can extend your coverage beyond the initial set term length at a higher premium without
      having to reprove your health. Your beneficiaries don't receive any benefit if you outlive the term policy.
    </p>
    <p>
      Coverage is the tax-free lump sum (also called death benefit) your beneficiaries would receive if you pass away
      during your life insurance policy's valid term.
    </p>
    <h4>What kind of life insurance policy quotes do you provide?</h4>
    <p>We provide quotes for term life, guaranteed issue final expense, and accidental death benefits.</p>
    <p>
      Term policies that cover you in the event of a death from both natural or accidental causes, anywhere you go in
      the world. The only reason the policy would not pay out would be if there were a suicide in the first two years of
      the policy.
    </p>
    <p>
      Guaranteed Issue Final Expense policies are permanent policies that cover you to age 100 and your acceptance is
      guaranteed, no health questions are asked. These policies are for smaller amounts of coverage meant to help with
      final expenses.{' '}
    </p>
    <p>
      ADB policies cover you in the event of a death from accidental causes only and you can keep the policy to age 80.
    </p>
    <p>
      There are no hidden exclusions in any of the policies we sell because we believe in being transparent so you can
      live on your terms and leave on your terms.
    </p>
  </div>
)

const ContactMessage = () => (
  <ThemeConsumer>
    {({ ctaStyles, colorBorder, colorSecondary, borderRadiusSmall }) => (
      <div
        className={classnames(
          styles.contactContainer,
          css`
            border-color: ${colorBorder};
            border-radius: ${borderRadiusSmall};
          `,
        )}
      >
        <div>
          <div className={styles.contactHeader}>
            <div>
              <h2
                className={classnames(css`
                  margin-top: 0;
                  margin-bottom: 10px;
                `)}
              >
                Contact
              </h2>
              <span>Talk to our expert - Shauna.</span>
            </div>
            <img src={ShaunaIcon} alt="Shauna" />
          </div>
          <div
            className={classnames(
              styles.contactInfoContainer,
              css`
                svg {
                  path {
                    fill: ${ctaStyles?.backgroundColor};
                  }
                }
              `,
            )}
          >
            <div className={classnames(styles.contactIconWrapper)}>
              <EmailIcon />
            </div>
            <div className={classnames(styles.contactActionWrapper)}>
              <h3>Email Us</h3>
              <a style={{ color: colorSecondary }} href={`mailto:${ContactLeapLife.Email}`}>
                {ContactLeapLife.Email}
              </a>
            </div>
          </div>
          <div
            className={classnames(
              styles.contactInfoContainer,
              css`
                svg {
                  path {
                    fill: ${ctaStyles?.backgroundColor};
                  }
                }
              `,
            )}
          >
            <div className={classnames(styles.contactIconWrapper)}>
              <PhoneIcon />
            </div>
            <div className={classnames(styles.contactActionWrapper)}>
              <h3>Call Us</h3>
              <a style={{ color: colorSecondary }} href={`tel:844-755-5327`}>
                (844) 755-5327
              </a>
            </div>
          </div>
        </div>
      </div>
    )}
  </ThemeConsumer>
)

export const Header = ({ children }) => (
  <ThemeConsumer>
    {({ colorBorder, borderRadiusSmall }) => (
      <div
        className={classnames(
          styles.header,
          css`
            border-color: ${colorBorder};
            border-radius: ${borderRadiusSmall};
          `,
        )}
      >
        {children}
      </div>
    )}
  </ThemeConsumer>
)
